<template>
  <div :class="containerClass">
    <page-header :title="$route.params.id ? 'Edit Society' : 'Add New Society'" :container-class="null" class="mb-16pt" />

    <b-form class="col-md-12 px-0 page-section pt-0" @submit.prevent="onSubmit">
      <b-form-group label="Name" label-for="name" label-class="form-label">
        <b-form-input id="name" placeholder="e.g. Student Nurses Association (SNA)" v-model.trim="form.name" required />
      </b-form-group>

      <b-form-row>
        <b-col md="4">
          <b-form-group label="Kind" label-for="kind" label-class="form-label">
            <v-select id="kind" class="form-control v-select-custom" label="text" v-model="form.kind" :reduce="k => k.value" :options="kindOptions" :clearable="false" />
          </b-form-group>
        </b-col>
        <b-col md="4">
          <b-form-group label="Discipline" label-for="discipline" label-class="form-label">
            <v-select id="discipline" class="form-control v-select-custom" label="text" v-model="form.discipline" :reduce="d => d.value" :options="disciplineOptions" :clearable="false" />
          </b-form-group>
        </b-col>
        <b-col md="4">
          <b-form-group label="Confidence" label-for="confidence" label-class="form-label">
            <v-select id="confidence" class="form-control v-select-custom" label="text" v-model="form.confidence" :reduce="c => c.value" :options="confidenceOptions" :clearable="false" />
            <small class="text-muted">Low confidence rows stay off TransitionU.</small>
          </b-form-group>
        </b-col>
      </b-form-row>

      <h5 class="so-section">Where it lives</h5>
      <b-form-group label="School" label-for="university" label-class="form-label">
        <v-select
          id="university"
          class="form-control v-select-custom"
          label="name"
          v-model="form.university"
          :reduce="u => u.id"
          placeholder="Type to search schools. Leave empty for a statewide body."
          :options="universityOptions"
          :loading="areUniversitiesLoading"
          :filterable="false"
          @search="onUniversitySearch"
        >
          <template #option="u">{{ u.name }}<span class="text-muted small"> · {{ [u.city, u.state].filter(Boolean).join(', ') }}</span></template>
        </v-select>
        <small class="text-muted">Campus chapters and pre-nursing clubs belong to a school. State associations have no school and show on every school page in their state.</small>
      </b-form-group>

      <b-form-row>
        <b-col md="3">
          <b-form-group label="State" label-for="state" label-class="form-label">
            <b-form-input id="state" placeholder="GA" maxlength="2" v-model.trim="form.state" :required="!form.university" style="text-transform: uppercase" />
          </b-form-group>
        </b-col>
        <b-col md="9">
          <b-form-group label="City" label-for="city" label-class="form-label">
            <b-form-input id="city" placeholder="e.g. Atlanta" v-model.trim="form.city" />
          </b-form-group>
        </b-col>
      </b-form-row>

      <h5 class="so-section">Public details</h5>
      <b-form-row>
        <b-col md="6">
          <b-form-group label="Website" label-for="website" label-class="form-label">
            <b-form-input id="website" type="url" placeholder="https://" v-model.trim="form.website" />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group label="Instagram" label-for="instagram" label-class="form-label">
            <b-form-input id="instagram" placeholder="@handle" v-model.trim="form.instagram" />
          </b-form-group>
        </b-col>
      </b-form-row>
      <b-form-group label="Who can join" label-for="membership_open_to" label-class="form-label">
        <b-form-input id="membership_open_to" placeholder="e.g. Nursing and pre-nursing students" v-model.trim="form.membership_open_to" />
      </b-form-group>
      <b-form-group label="Description" label-for="description" label-class="form-label">
        <b-form-textarea id="description" :rows="4" placeholder="What the society does, in the society's own words where possible." v-model.trim="form.description" />
      </b-form-group>
      <b-form-group label="Source URL" label-for="source_url" label-class="form-label">
        <b-form-input id="source_url" type="url" placeholder="Page these details came from" v-model.trim="form.source_url" />
      </b-form-group>
      <b-form-group label="Logo URL" label-for="logo_url" label-class="form-label">
        <b-form-input id="logo_url" type="url" placeholder="https://… (PNG or SVG). Leave empty to use the school's logo." v-model.trim="form.logo_url" />
      </b-form-group>
      <b-form-row>
        <b-col md="8">
          <b-form-group label="Community link" label-for="community_url" label-class="form-label">
            <b-form-input id="community_url" type="url" placeholder="Discord, GroupMe or a TransitionU community" v-model.trim="form.community_url" />
            <small class="text-muted">Shown as "Join the community" on the society page.</small>
          </b-form-group>
        </b-col>
        <b-col md="4">
          <b-form-group label="Claim state" label-for="claim_state" label-class="form-label">
            <v-select id="claim_state" class="form-control v-select-custom" label="text" v-model="form.claim_state" :reduce="c => c.value" :options="claimOptions" :clearable="false" />
            <small class="text-muted">Unclaimed pages invite the society to claim them. Claiming as an organization comes later.</small>
          </b-form-group>
        </b-col>
      </b-form-row>

      <h5 class="so-section">Connections</h5>
      <b-form-group label="Paths shown on this society" label-for="pathways" label-class="form-label">
        <v-select id="pathways" class="form-control v-select-custom" label="title" v-model="form.pathways" :reduce="p => p.id" multiple
          placeholder="Select paths" :options="pathwayOptions" :loading="arePathwaysLoading" />
        <small class="text-muted">Paths into the society's own school show automatically. Add paths here to feature them too; a path can sit on several societies.</small>
      </b-form-group>
      <b-form-group label="Where members go next (feeder programs)" label-for="feeds_into" label-class="form-label">
        <v-select
          id="feeds_into"
          class="form-control v-select-custom"
          label="display_label"
          v-model="form.feeds_into"
          :reduce="o => o.id"
          multiple
          placeholder="Type a school or program name"
          :options="offeredOptions"
          :loading="areOfferedLoading"
          :filterable="false"
          @search="onOfferedSearch"
        />
        <small class="text-muted">For a pre-nursing club at a campus without a nursing program: the university programs its members apply to. The club then shows as a feeder on those school pages.</small>
      </b-form-group>

      <h5 class="so-section">Internal contact (never public)</h5>
      <b-form-row>
        <b-col md="6">
          <b-form-group label="Email" label-for="email" label-class="form-label">
            <b-form-input id="email" type="email" v-model.trim="form.email" />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group label="Phone" label-for="phone" label-class="form-label">
            <b-form-input id="phone" v-model.trim="form.phone" />
          </b-form-group>
        </b-col>
      </b-form-row>
      <b-form-group label="Faculty advisor" label-for="advisor" label-class="form-label">
        <b-form-input id="advisor" placeholder="Name and role" v-model.trim="form.advisor" />
      </b-form-group>
      <b-form-group label="Notes" label-for="notes" label-class="form-label">
        <b-form-textarea id="notes" :rows="3" v-model.trim="form.notes" />
      </b-form-group>

      <h5 class="so-section">Status</h5>
      <b-form-group label-class="form-label">
        <b-form-checkbox v-model="form.is_active" name="is-active-switch" switch>Active (shown on TransitionU)</b-form-checkbox>
      </b-form-group>

      <b-btn variant="primary" :disabled="isLoading" style="width: 150px" type="submit" class="btn-normal">
        <i v-if="isLoading" class="fas fa-circle-notch fa-spin"></i>
        <span v-else>{{ $route.params.id ? $t('update') : $t('add') }}</span>
      </b-btn>
    </b-form>
  </div>
</template>

<script>
import { debounce, get } from 'lodash';
import { mapActions } from 'vuex';
import PageHeader from '@/components/Ui/PageHeader.vue';
import Page from '@/components/Page.vue';

export default {
  components: { PageHeader },
  name: 'PostSociety',
  extends: Page,

  data() {
    return {
      isLoading: false,
      areUniversitiesLoading: false,
      areOfferedLoading: false,
      arePathwaysLoading: false,
      universityOptions: [],
      offeredOptions: [],
      pathwayOptions: [],
      kindOptions: [
        { value: 'school_chapter', text: 'School chapter' },
        { value: 'pre_nursing_club', text: 'Pre-nursing club' },
        { value: 'state_association', text: 'State association' },
        { value: 'national', text: 'National' },
        { value: 'other', text: 'Other' },
      ],
      disciplineOptions: [
        { value: 'nursing', text: 'Nursing' },
        { value: 'dental', text: 'Dental' },
        { value: 'pharmacy', text: 'Pharmacy' },
        { value: 'med', text: 'Medicine' },
        { value: 'pa', text: 'Physician assistant' },
      ],
      claimOptions: [
        { value: 'unclaimed', text: 'Unclaimed' },
        { value: 'claimed', text: 'Claimed' },
      ],
      confidenceOptions: [
        { value: 'high', text: 'High: official page with contact' },
        { value: 'med', text: 'Medium: confirmed, thin contact' },
        { value: 'low', text: 'Low: inferred' },
      ],
      form: {
        name: '',
        kind: 'school_chapter',
        discipline: 'nursing',
        confidence: 'med',
        university: null,
        state: '',
        city: '',
        website: '',
        instagram: '',
        membership_open_to: '',
        description: '',
        source_url: '',
        community_url: '',
        logo_url: '',
        claim_state: 'unclaimed',
        pathways: [],
        feeds_into: [],
        email: '',
        phone: '',
        advisor: '',
        notes: '',
        is_active: true,
      },
    };
  },

  methods: {
    get,
    ...mapActions('societies', ['getSociety', 'createSociety', 'updateSociety']),
    ...mapActions('uni', ['getAllUnis', 'getAllUniOfferedPrograms']),
    ...mapActions('pathways', ['getAllPathways']),

    /** Keep already chosen rows in the option list so v-select can label them. */
    mergeOptions(current, incoming, key = 'id') {
      const seen = new Set(incoming.map(o => o[key]));
      return [...incoming, ...current.filter(o => !seen.has(o[key]) && this.isChosen(o, key))];
    },
    isChosen(o, key) {
      return o[key] === this.form.university || this.form.feeds_into.includes(o[key]);
    },

    async searchUniversities(term) {
      this.areUniversitiesLoading = true;
      try {
        const response = await this.getAllUnis({ limit: 30, ...(term && { search: term }) });
        const rows = get(response, 'data.results', []).map(u => ({ id: u.id, name: u.name, city: u.city, state: u.state }));
        this.universityOptions = this.mergeOptions(this.universityOptions, rows);
      } catch (_err) {
        /* keep what we have */
      }
      this.areUniversitiesLoading = false;
    },
    onUniversitySearch: debounce(function(term) {
      if (term && term.length >= 2) this.searchUniversities(term);
    }, 350),

    async searchOffered(term) {
      this.areOfferedLoading = true;
      try {
        const response = await this.getAllUniOfferedPrograms({ limit: 40, ...(term && { search: term }) });
        const rows = get(response, 'data.results', []).map(o => ({
          id: o.id,
          display_label: `${get(o, 'university.name', '—')} — ${get(o, 'program.title', '—')}`,
        }));
        this.offeredOptions = this.mergeOptions(this.offeredOptions, rows);
      } catch (_err) {
        /* keep what we have */
      }
      this.areOfferedLoading = false;
    },
    onOfferedSearch: debounce(function(term) {
      if (term && term.length >= 2) this.searchOffered(term);
    }, 350),

    async fetchPathways() {
      this.arePathwaysLoading = true;
      try {
        const response = await this.getAllPathways({ limit: 500, ordering: 'title' });
        this.pathwayOptions = get(response, 'data.results', []).map(p => ({ id: p.id, title: p.title }));
      } catch (_err) {
        this.pathwayOptions = [];
      }
      this.arePathwaysLoading = false;
    },

    async loadSociety(id) {
      try {
        const response = await this.getSociety(id);
        const data = get(response, 'data', {});
        const uni = data.university || null;
        const feeders = data.feeds_into || [];
        this.form = {
          name: data.name || '',
          kind: data.kind || 'school_chapter',
          discipline: data.discipline || 'nursing',
          confidence: data.confidence || 'med',
          university: uni ? uni.id : null,
          state: data.state || '',
          city: data.city || '',
          website: data.website || '',
          instagram: data.instagram || '',
          membership_open_to: data.membership_open_to || '',
          description: data.description || '',
          source_url: data.source_url || '',
          community_url: data.community_url || '',
          logo_url: data.logo_url || '',
          claim_state: data.claim_state || 'unclaimed',
          pathways: (data.pathways || []).map(p => p.id),
          feeds_into: feeders.map(f => f.id),
          email: data.email || '',
          phone: data.phone || '',
          advisor: data.advisor || '',
          notes: data.notes || '',
          is_active: data.is_active !== false,
        };
        if (uni) this.universityOptions = [{ id: uni.id, name: uni.name, city: uni.city, state: uni.state }];
        this.offeredOptions = feeders.map(f => ({ id: f.id, display_label: f.display_label }));
      } catch (_err) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
    },

    async onSubmit() {
      this.isLoading = true;
      try {
        const payload = { ...this.form, state: (this.form.state || '').toUpperCase() || null };
        if (this.$route.params.id) {
          await this.updateSociety({ id: this.$route.params.id, data: payload });
          this.makeToast({ variant: 'success', msg: 'Society updated.' });
        } else {
          await this.createSociety(payload);
          this.makeToast({ variant: 'success', msg: 'Society created.' });
        }
        this.$router.push({ name: 'sa-manage-societies' });
      } catch (err) {
        const detail = get(err, 'response.data.detail');
        const fieldErrors = get(err, 'response.data');
        let msg = this.$t('generalMsgs.genErrorMsg');
        if (detail) {
          msg = detail;
        } else if (fieldErrors && typeof fieldErrors === 'object') {
          const firstKey = Object.keys(fieldErrors)[0];
          const firstVal = fieldErrors[firstKey];
          msg = `${firstKey}: ${Array.isArray(firstVal) ? firstVal[0] : String(firstVal)}`;
        }
        this.makeToast({ variant: 'danger', msg });
      }
      this.isLoading = false;
    },
  },

  async mounted() {
    await this.fetchPathways();
    if (this.$route.params.id) {
      await this.loadSociety(this.$route.params.id);
    }
  },
};
</script>

<style lang="scss" scoped>
.so-section { margin: 26px 0 12px; padding-bottom: 8px; border-bottom: 1px solid #e9ecef; font-size: 15px; font-weight: 700; }
</style>
